/* TDB Stock Register · "Other —" escape-hatch UI components
 *
 * Two compact pieces:
 *   1. <OtherRowDraft />  · the inline draft row Roji sees when she
 *      clicks "+ Add Other (Raw/Packaging/Consumable)". Has a custom-name
 *      input that fuzzy-matches on blur against master items + prior
 *      Others from the last 3 months. Soft suggestion appears under
 *      the input · Roji can accept or proceed with her own typing.
 *   2. <PendingPromotionsBadge />  · sits at the top of the register.
 *      Counts custom_names that appeared in ≥2 of the last 3 months.
 *      Click opens a modal with Promote / Reject buttons per row.
 *      Decisions write to mis_audit_log.
 */
import { useState, useEffect, useCallback } from 'react';
import { Sparkles, AlertCircle, X, ChevronRight, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const PLACEHOLDER_BY_CAT = {
  raw: 'Other — Raw Material',
  packaging: 'Other — Packaging',
  consumable: 'Other — Consumable',
};

const CATEGORY_LABEL = {
  raw: '🍞 Raw',
  packaging: '📦 Packaging',
  consumable: '🧹 Consumable',
};


// ────────────────────────────────────────────────────────────────
// Draft row for adding ONE "Other" entry · embedded inside section
// ────────────────────────────────────────────────────────────────
export function OtherRowDraft({
  monthYM,
  city,
  category,
  onSaved,
  onCancel,
}) {
  const [customName, setCustomName] = useState('');
  const [openingQty, setOpeningQty] = useState('');
  const [purchasesQty, setPurchasesQty] = useState('');
  const [closingQty, setClosingQty] = useState('');
  const [rate, setRate] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const placeholder = PLACEHOLDER_BY_CAT[category];

  const runFuzzyMatch = useCallback(async () => {
    const q = customName.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    try {
      const r = await api.get('/tdb-mis/stock/register/fuzzy-match', {
        params: { text: q, placeholder, limit: 4 },
      });
      setSuggestions(r.data?.suggestions || []);
    } catch (e) {
      // silent · suggestion is non-essential
      setSuggestions([]);
    }
  }, [customName, placeholder]);

  const save = async () => {
    setErr('');
    if (!customName.trim()) {
      setErr('Type what you bought (e.g. "Saffron", "Diwali tin")');
      return;
    }
    setBusy(true);
    try {
      await api.put('/tdb-mis/stock/register', {
        month_ym: monthYM,
        city,
        item: placeholder,
        custom_name: customName.trim(),
        opening_qty: parseFloat(openingQty) || 0,
        purchases_qty: parseFloat(purchasesQty) || 0,
        closing_qty: closingQty === '' ? null : parseFloat(closingQty),
        rate: parseFloat(rate) || 0,
      });
      onSaved && onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`tdb-other-draft-${category}-${city.toLowerCase()}`}
      className="p-2 rounded-md border-2 border-dashed border-amber-400 bg-amber-50/40 text-xs"
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-medium text-amber-900 truncate flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          New Other · {placeholder.replace('Other — ', '')} · {city}
        </span>
        <button
          type="button"
          data-testid={`tdb-other-draft-cancel-${category}-${city.toLowerCase()}`}
          onClick={onCancel}
          className="text-amber-700 hover:text-amber-900"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      <input
        data-testid={`tdb-other-draft-name-${category}-${city.toLowerCase()}`}
        type="text"
        placeholder="What was it? e.g. Saffron, Diwali tin, edible gold dust"
        value={customName}
        onChange={(e) => setCustomName(e.target.value)}
        onBlur={runFuzzyMatch}
        maxLength={80}
        className="w-full text-[11px] px-2 py-1 rounded border border-amber-300 bg-white mb-1"
      />

      {suggestions.length > 0 && (
        <div
          data-testid={`tdb-other-draft-suggestions-${category}-${city.toLowerCase()}`}
          className="mb-1.5 rounded border border-amber-200 bg-white p-1.5"
        >
          <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium mb-0.5">
            Did you mean?
          </div>
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setCustomName(s.display); setSuggestions([]); }}
              className="block w-full text-left text-[11px] px-1.5 py-0.5 rounded hover:bg-amber-100"
            >
              <span className="font-medium text-amber-900">{s.display}</span>
              <span className="text-[10px] text-amber-700 ml-1">· {s.hint}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-4 gap-1 mb-1">
        <input
          type="number" step="0.001" placeholder="Opening"
          value={openingQty}
          onChange={(e) => setOpeningQty(e.target.value)}
          className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-300 bg-white"
        />
        <input
          type="number" step="0.001" placeholder="Purch."
          value={purchasesQty}
          onChange={(e) => setPurchasesQty(e.target.value)}
          className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-300 bg-white"
        />
        <input
          type="number" step="0.001" placeholder="Closing"
          value={closingQty}
          onChange={(e) => setClosingQty(e.target.value)}
          className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-300 bg-white"
        />
        <input
          type="number" step="0.01" placeholder="₹ rate"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="text-right text-[11px] px-1 py-0.5 rounded border border-amber-300 bg-white"
        />
      </div>

      {err && (
        <div className="text-[11px] text-red-700 mb-1">{err}</div>
      )}

      <button
        type="button"
        data-testid={`tdb-other-draft-save-${category}-${city.toLowerCase()}`}
        onClick={save}
        disabled={busy}
        className="w-full text-[11px] font-medium px-2 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-1"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
        Save Other entry
      </button>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────
// Pending-promotions badge + modal
// ────────────────────────────────────────────────────────────────
export function PendingPromotionsBadge({ onAction }) {
  const [candidates, setCandidates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/tdb-mis/stock/pending-promotions');
      setCandidates(r.data?.candidates || []);
    } catch (e) {
      setCandidates([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const promote = async (c) => {
    const canonical = window.prompt(
      `Promote "${c.custom_name}" to a permanent master item?\n\n` +
      `Category: ${c.category}\n` +
      `Seen in: ${c.month_count} of last 3 months\n\n` +
      `Master item name (edit if you'd like a cleaner spelling):`,
      c.custom_name,
    );
    if (!canonical) return;
    const trackDaily = window.confirm(
      `Make "${canonical}" daily-tracked too?\n\nOK = yes (Britto sees it daily)\nCancel = no (monthly only)`,
    );
    setBusyKey(`promote|${c.custom_name_norm}`);
    try {
      await api.post('/tdb-mis/stock/promote-to-master', {
        placeholder: c.placeholder,
        custom_name: c.custom_name,
        item_name_canonical: canonical.trim(),
        unit: c.unit || 'kg',
        track_daily: trackDaily,
      });
      await load();
      onAction && onAction();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Promotion failed');
    } finally {
      setBusyKey('');
    }
  };

  const reject = async (c) => {
    if (!window.confirm(
      `Reject "${c.custom_name}" — confirm this stays as a one-off "Other" entry?\n\n` +
      `The system will stop asking about it.`,
    )) return;
    setBusyKey(`reject|${c.custom_name_norm}`);
    try {
      await api.post('/tdb-mis/stock/reject-promotion', {
        placeholder: c.placeholder,
        custom_name: c.custom_name,
      });
      await load();
      onAction && onAction();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyKey('');
    }
  };

  if (!loaded || candidates.length === 0) return null;

  return (
    <>
      <button
        type="button"
        data-testid="tdb-stock-pending-promotions-badge"
        onClick={() => setOpen(true)}
        className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 flex items-center gap-1 font-medium"
      >
        <AlertCircle className="w-3 h-3" />
        {candidates.length} pending promotion{candidates.length === 1 ? '' : 's'}
      </button>

      {open && (
        <div
          data-testid="tdb-stock-pending-promotions-modal"
          className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-2xl border border-slate-200 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  Promote to master?
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  These "Other" entries have appeared 2+ times in the last 3 months.
                  Promote if they're becoming regulars · reject if one-offs.
                </p>
              </div>
              <button
                type="button"
                data-testid="tdb-stock-pending-promotions-close"
                onClick={() => setOpen(false)}
                className="text-slate-500 hover:text-slate-900"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-2">
              {candidates.map((c, i) => (
                <div
                  key={`${c.placeholder}|${c.custom_name_norm}`}
                  data-testid={`tdb-stock-promotion-row-${i}`}
                  className="rounded-lg border border-slate-200 p-3 bg-slate-50/60"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="font-medium text-slate-900 text-sm">
                        {c.custom_name}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {c.placeholder} · {c.month_count}/3 months · {c.occurrences} entries · cities: {c.cities?.join(', ')}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Seen in: {c.months?.sort().join(', ')}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        data-testid={`tdb-stock-promotion-reject-${i}`}
                        onClick={() => reject(c)}
                        disabled={!!busyKey}
                        className="text-xs px-2 py-1 rounded-md border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50"
                      >
                        Reject · one-off
                      </button>
                      <button
                        type="button"
                        data-testid={`tdb-stock-promotion-promote-${i}`}
                        onClick={() => promote(c)}
                        disabled={!!busyKey}
                        className="text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        Promote <ChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


export const PLACEHOLDER_NAMES = Object.values(PLACEHOLDER_BY_CAT);
export const CATEGORY_LABELS = CATEGORY_LABEL;
